import { createContext, useContext, useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "rodental_cookie_consent";
const CONSENT_VERSION = "1.0";

/**
 * Categorii identificate în audit (august 2026):
 * - necessary: localStorage `rodental_token` (autentificare admin) — permanent activ
 * - thirdParty: iframe Google Maps (poate seta cookies Google) — opt-in
 *
 * NU există: Google Analytics, Meta Pixel, marketing tracking, statistici.
 */
const DEFAULT_CONSENT = {
  necessary: true,
  thirdParty: false,
  version: CONSENT_VERSION,
  timestamp: null,
};

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(DEFAULT_CONSENT);
  const [hasChosen, setHasChosen] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.version === CONSENT_VERSION) {
          setConsent({ ...DEFAULT_CONSENT, ...parsed });
          setHasChosen(true);
        }
      }
    } catch (e) { /* corrupted storage — ignore */ }
  }, []);

  const persist = useCallback((next) => {
    const withMeta = { ...next, version: CONSENT_VERSION, timestamp: new Date().toISOString() };
    setConsent(withMeta);
    setHasChosen(true);
    setPreferencesOpen(false);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(withMeta)); } catch (e) { /* ignore */ }
  }, []);

  const acceptAll = useCallback(() => persist({ necessary: true, thirdParty: true }), [persist]);
  const rejectOptional = useCallback(() => persist({ necessary: true, thirdParty: false }), [persist]);
  const savePreferences = useCallback((choices) => persist({ necessary: true, thirdParty: !!choices.thirdParty }), [persist]);
  const reopenPreferences = useCallback(() => setPreferencesOpen(true), []);
  const closePreferences = useCallback(() => setPreferencesOpen(false), []);

  const value = { consent, hasChosen, preferencesOpen, acceptAll, rejectOptional, savePreferences, reopenPreferences, closePreferences };
  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error("useCookieConsent must be used within CookieConsentProvider");
  return ctx;
}
